// ============================================================================
// Price Breakdown dialog — the counter's answer to "why this much?".
// ----------------------------------------------------------------------------
// Shows, in plain language, how the amount on the receipt screen was arrived at:
// which rule decided the months charged, the arithmetic behind each figure, and
// how the money handed over is applied (interest → insurance → principal). Built
// to be TURNED TOWARDS THE CUSTOMER — generous type, no jargon, no internal
// field names — and printable so they can take it with them.
//
// Everything rendered comes from buildReceiptBreakdown (receiptBreakdown.js),
// which reads the engine's own output; this file is presentation only.
// ============================================================================
import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { money, printReceiptBreakdown } from "./receiptBreakdown";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_LIGHT = "#DBEAFE";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SUCCESS = "#059669";
const SUCCESS_BG = "#ECFDF5";
const WARN = "#D97706";
const WARN_BG = "#FFFBEB";

const fmtDate = (d) =>
  d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : "—";

// Section heading — small, uppercase, muted.
const Head = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      fontWeight: 700,
      color: INK_MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      mt: 2,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

// label ─ value row, optionally emphasised or with a sub-note.
const Row = ({ label, value, note, strong, valueColor }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 2,
      py: 0.5,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.85rem",
          color: strong ? INK : INK_SOFT,
          fontWeight: strong ? 700 : 500,
        }}
      >
        {label}
      </Typography>
      {note && (
        <Typography sx={{ fontSize: "0.7rem", color: INK_MUTED }}>
          {note}
        </Typography>
      )}
    </Box>
    <Typography
      sx={{
        fontSize: strong ? "0.95rem" : "0.85rem",
        fontWeight: strong ? 800 : 600,
        color: valueColor || (strong ? INK : INK_SOFT),
        fontFamily: "monospace",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ReceiptBreakdownDialog = ({ open, onClose, model }) => {
  const theme = useTheme();
  // Narrow screens (phones, and small-tablet panes like the Surface Duo's
  // 540×720) get a tighter, more compact layout — but NEVER a fullScreen dialog.
  // This is a thing you show someone across the counter, so it has to read as a
  // card floating over the receipt, not as a takeover of the whole app.
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  if (!model) return null;
  const { header, basis, rule, lines, totals, allocation, outcome } = model;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper" // title + actions stay put; only the body scrolls
      PaperProps={{
        sx: {
          borderRadius: "14px",
          // Small margins keep the backdrop visible on every edge, so it still
          // reads as a pop-up at 540px wide; the body scrolls inside.
          m: { xs: 1.25, sm: 3 },
          width: { xs: "calc(100% - 20px)", sm: "calc(100% - 48px)" },
          maxHeight: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.25 },
          px: { xs: 1.75, sm: 3 },
          py: { xs: 1.25, sm: 1.5 },
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            width: { xs: 30, sm: 34 },
            height: { xs: 30, sm: 34 },
            flexShrink: 0,
            borderRadius: "9px",
            bgcolor: BLUE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CalculateIcon
            sx={{ color: "#fff", fontSize: isCompact ? 17 : 19 }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "0.92rem", sm: "1.02rem" },
              lineHeight: 1.25,
              color: INK,
            }}
          >
            How this amount is calculated
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: INK_MUTED, display: "block", lineHeight: 1.35 }}
          >
            Ticket {header.voucher}
            {header.receiptNo ? ` · Receipt ${header.receiptNo}` : ""} ·{" "}
            {header.customerName}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1.75, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        {/* The rule that decided the charge — the thing customers question. */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: "10px",
            bgcolor: BLUE_PALE,
            border: `1px solid ${BLUE_LIGHT}`,
          }}
        >
          <Typography
            sx={{ fontWeight: 800, color: BLUE_DARK, fontSize: "0.9rem" }}
          >
            {rule.title}
          </Typography>
          <Typography sx={{ color: INK_SOFT, fontSize: "0.82rem", mt: 0.5 }}>
            {rule.detail}
          </Typography>
        </Box>

        {header.approximate && (
          <Typography
            sx={{ mt: 1, fontSize: "0.72rem", color: WARN, fontWeight: 600 }}
          >
            Offline estimate — the counter will confirm the final figure.
          </Typography>
        )}

        <Head>Basis of the calculation</Head>
        <Box sx={{ px: 0.25 }}>
          {basis.map((b) => (
            <Row
              key={b.label}
              label={b.label}
              value={b.value}
              note={b.note}
              strong={b.strong}
            />
          ))}
        </Box>

        <Head>Amount due</Head>
        <Stack divider={<Divider sx={{ borderColor: BORDER }} />} spacing={0}>
          {lines.map((l) => (
            <Box
              key={l.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                py: 0.9,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: "0.85rem", fontWeight: 600, color: INK }}
                >
                  {l.label}
                </Typography>
                <Typography sx={{ fontSize: "0.74rem", color: INK_MUTED }}>
                  {l.formula}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "monospace",
                  color: INK,
                  whiteSpace: "nowrap",
                }}
              >
                {money(l.amount)}
              </Typography>
            </Box>
          ))}
        </Stack>
        {totals.discount > 0 && (
          <Row
            label="Less: interest discount"
            value={`− ${money(totals.discount)}`}
          />
        )}
        {totals.charges > 0 && (
          <Row label="Add: other charges" value={money(totals.charges)} />
        )}
        <Divider sx={{ my: 0.75, borderColor: BORDER }} />
        <Row label="Total payable" value={money(totals.payable)} strong />

        <Head>Amount received &amp; how it is applied</Head>
        <Row label="Received now" value={money(totals.received)} strong />
        <Box
          sx={{
            pl: 1.5,
            borderLeft: `2px solid ${BLUE_LIGHT}`,
            ml: 0.5,
            mt: 0.5,
          }}
        >
          {allocation.map((a) => (
            <Row key={a.label} label={a.label} value={money(a.amount)} />
          ))}
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED, mt: 0.75 }}>
          Money is applied to interest first, then insurance, and whatever is
          left reduces the principal.
        </Typography>

        {/* Outcome — what the customer walks away with. */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: "10px",
            bgcolor: outcome.shortfall > 0 ? WARN_BG : SUCCESS_BG,
            border: `1px solid ${
              outcome.shortfall > 0 ? "#FDE68A" : "#A7F3D0"
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{ fontSize: "0.85rem", color: INK_SOFT, fontWeight: 600 }}
            >
              Interest paid up to
            </Typography>
            <Chip
              size="small"
              label={fmtDate(outcome.paidUpToDate)}
              sx={{
                bgcolor: outcome.shortfall > 0 ? "#FEF3C7" : "#D1FAE5",
                color: outcome.shortfall > 0 ? "#92400E" : SUCCESS,
                fontWeight: 700,
              }}
            />
          </Box>
          <Row
            label="Balance principal"
            value={money(outcome.principalAfter)}
          />
          {outcome.shortfall > 0 && (
            <>
              <Divider sx={{ my: 0.5, borderColor: "#FDE68A" }} />
              {outcome.shortfallInterest > 0 && (
                <Row
                  label="Interest still unpaid"
                  value={money(outcome.shortfallInterest)}
                  valueColor="#92400E"
                />
              )}
              {outcome.shortfallInsurance > 0 && (
                <Row
                  label="Insurance still unpaid"
                  value={money(outcome.shortfallInsurance)}
                  valueColor="#92400E"
                />
              )}
              <Typography
                sx={{ fontSize: "0.72rem", color: "#92400E", mt: 0.5 }}
              >
                This amount is not waived — it is added to the next receipt's
                dues.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      {/* Actions share the row evenly on narrow screens (the two buttons would
          otherwise crowd against each other at 540px) and sit right-aligned,
          Print first, from sm up. */}
      <DialogActions
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: { xs: "stretch", sm: "flex-end" },
          px: { xs: 1.75, sm: 3 },
          py: { xs: 1.25, sm: 1.5 },
          borderTop: `1px solid ${BORDER}`,
          // Kill MUI's default sibling margin so `gap` is the only spacing.
          "& > :not(style) ~ :not(style)": { ml: 0 },
          "& > *": { flex: { xs: 1, sm: "0 0 auto" } },
        }}
      >
        <Button
          onClick={() => printReceiptBreakdown(model)}
          startIcon={<PrintIcon />}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "9px",
            whiteSpace: "nowrap",
          }}
        >
          {isCompact ? "Print" : "Print for customer"}
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "9px",
            bgcolor: BLUE,
            "&:hover": { bgcolor: BLUE_DARK },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptBreakdownDialog;
